import { KText } from '@/ui-library/components/text';
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Box,
  Icon,
  useDisclosure,
  useOutsideClick,
  Flex,
} from '@chakra-ui/react';
import { Check, ChevronDown, SwitchVertical01 } from '@kula-ai/icons-core';
import { useEffect, useRef, useState } from 'react';
import { getFilterTriggerStyles } from '@/components/filters_revamp/utils/filterTriggerStyles';

const DEFAULT_SORT_LABEL = 'Sort by:';

// A sort is always in effect, so the collapsed trigger takes the applied
// (highlighted) state — the same one the filter trigger uses once a filter is
// set, so the two read as the same control at a glance.
const SORT_TRIGGER_STYLES = getFilterTriggerStyles(true);

export interface SortOption {
  label?: string;
  element?: string;
  order?: string;
}

interface SortByFilterProps {
  sort?: SortOption;
  filterActions?: (payload: { type: 'sort'; option: SortOption }) => void;
  options?: SortOption[];
  menuItemStyles?: Record<string, unknown>;
  /** Trigger prefix. Narrow columns pass a shorter one. */
  label?: string;
}

export const SortByFilter = ({
  sort = {},
  filterActions = () => {},
  options = [],
  menuItemStyles = {},
  label = DEFAULT_SORT_LABEL,
}: SortByFilterProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { isOpen, onClose, onToggle } = useDisclosure();
  const [selectedSort, setSelectedSort] = useState<SortOption>(() => {
    return Object.keys(sort).length > 0
      ? sort
      : options.length > 0
        ? options[0]
        : {};
  });

  useEffect(() => {
    setSelectedSort(sort);
  }, [sort]);

  const handleSortBy = (option: SortOption = {}) => {
    filterActions({ type: 'sort', option });
    setSelectedSort(option);
    onClose();
  };

  useOutsideClick({
    ref: ref,
    handler: () => onClose(),
  });

  const renderHeader = () => {
    return (
      <MenuButton onClick={onToggle} aria-label={label}>
        <Flex gap=".25em" alignItems="center">
          {/* Below md the sort collapses to its icon — the label plus the
              selected option runs to ~40 characters and pushed search onto a
              row of its own. The icon carries the same menu, in the same
              pill the filter trigger uses so the two read as one control set. */}
          <Flex
            display={{ base: 'flex', md: 'none' }}
            alignItems="center"
            justifyContent="center"
            // Matches TableCardToggle and the collapsed search exactly — they
            // sit next to each other, so any difference reads as misalignment.
            w="1.75em"
            h="1.75em"
            borderRadius="6px"
            bg={SORT_TRIGGER_STYLES.bg}
          >
            <Icon
              as={SwitchVertical01}
              color={SORT_TRIGGER_STYLES.contentColor}
            />
          </Flex>
          <KText
            size="sm-semi-bold"
            color="secondary-800"
            display={{ base: 'none', md: 'block' }}
          >
            {label}
          </KText>
          <KText onClick={onToggle} display={{ base: 'none', md: 'block' }}>
            {selectedSort?.label}
          </KText>
          <Icon
            as={ChevronDown}
            display={{ base: 'none', md: 'block' }}
            height="16px"
            width="16px"
            color="secondary-900"
          />
        </Flex>
      </MenuButton>
    );
  };

  const renderOption = (option: SortOption = {}) => {
    const { element, order } = option;
    const isOptionSelected =
      selectedSort.element === element && selectedSort.order === order;
    return (
      <MenuItem
        key={element + order}
        onClick={() => handleSortBy(option)}
        minW="220px"
        _hover={{ background: 'secondary-50' }}
        {...menuItemStyles}
      >
        <Box flex="1">
          <Flex
            gap="3px"
            w="inherit"
            alignItems="center"
            justifyContent="space-between"
          >
            <KText
              color={isOptionSelected ? 'primary-500' : 'secondary-700'}
              size={isOptionSelected ? 'sm-medium' : 'sm'}
            >
              {option.label}
            </KText>
            {isOptionSelected && (
              <Icon as={Check} color="ats-primary" width="20px" height="20px" />
            )}
          </Flex>
        </Box>
      </MenuItem>
    );
  };

  return (
    <Flex alignItems="center" ref={ref}>
      <Menu placement="bottom-end" isOpen={isOpen}>
        {renderHeader()}
        <MenuList zIndex="3">{options.map(renderOption)}</MenuList>
      </Menu>
    </Flex>
  );
};
